import { basistheory } from "@basis-theory/web-elements";
import { SUPPORTED_COUNTRIES } from "./constants";
import { Api } from "./services/services";
import { DEFAULT_THEME, handlePseudoElementStyles, validate } from "./util";
import { CARD_FORM_SCHEMA } from "./schemas/cardFormSchema";
import { generateBankAccountFormSchema } from "./schemas/bankAccountFormSchema";
import { Card } from "./util/payments/cardUtil";
import { BankAccount } from "./util/payments/bankAccountUtil";

// import types for npm rollup
import "./types.d";

// keep track of instance of bt and primary api key across all OdinPay instances
/**
 * @type {Promise<(import("@basis-theory/web-elements").BasisTheoryElements)> | undefined} bt
 */
let bt;
let btApiKey;

const getDecodedKey = (key) => {
	if (!key) {
		throw Error("No key provided");
	}

	let decodedToken;
	try {
		decodedToken = JSON.parse(atob(key));
	} catch (e) {
		throw Error("Badly formatted key");
	}

	const { basisTheoryKey, token, apiEndpoint } = decodedToken;
	if (!basisTheoryKey || !token || !apiEndpoint) {
		throw Error("Invalid Key");
	}
	return decodedToken;
};

const getValidateOptions = (options) => {
	// TODO: This is to be supported until our integrators have fully implemented Multi-country support
	if (options.input) {
		return {
			theme: options,
			country: SUPPORTED_COUNTRIES.US
		};
	}

	const { theme = DEFAULT_THEME, country = SUPPORTED_COUNTRIES.US } = options;
	const supportedCountries = Object.values(SUPPORTED_COUNTRIES);
	if (!supportedCountries.includes(country.toUpperCase())) {
		throw Error(`Unsupported country. Supported countries are ${supportedCountries.join(", ")}`);
	}

	return {
		theme,
		country
	};
};

/**
 * @typedef {Object} OdinPayConfig
 * @property {odinTheme} [theme] - optional theme for customizing certain CSS.
 * @property {(SUPPORTED_COUNTRIES.US|SUPPORTED_COUNTRIES.CA)} country
 */

/**
 * initialize Odin Pay object.
 * @type {function}
 * @param {string} key - api key for OdinPay.
 * @param {OdinPayConfig} options
 * @returns {odinPayObject} - the Odin Pay object to use when creating forms and components
 */
// eslint-disable-next-line no-undef
const OdinPay = (key, options = {}) => {
	const { basisTheoryKey, token, apiEndpoint, env } = getDecodedKey(key);

	const { theme, country } = getValidateOptions(options);

	const customTheme = { ...DEFAULT_THEME, ...theme };

	handlePseudoElementStyles(customTheme);

	if (!btApiKey) {
		btApiKey = basisTheoryKey;
		let btConfig;
		// override bt environment if env is provided and not prod, else leave undefined for default (prod) behavior
		if (env && env !== "prod") {
			btConfig = { environment: "test" };
		}
		bt = basistheory(basisTheoryKey, btConfig);
	} else if (btApiKey !== basisTheoryKey) {
		throw Error("Cannot initialize for multiple tokenization environments at the same time!");
	}

	const api = Api(apiEndpoint, token);

	return {
		/**
		 * @type {createCardForm}
		 */
		createCardForm: async (config) => {
			const { isValid: isConfigValid, errors: configErrors } = validate(CARD_FORM_SCHEMA, config);

			if (!isConfigValid) {
				console.error(configErrors);
				return;
			}

			try {
				const card = new Card(await bt, config, customTheme, api);
				await card.init();
			} catch (e) {
				console.error(e);
			}
		},

		/**
		 * @type {createBankAccountForm}
		 */
		createBankAccountForm: async (config) => {
			const { isValid, errors } = validate(generateBankAccountFormSchema(country), config);

			if (!isValid) {
				console.error(errors);
				return;
			}

			try {
				const bankAccount = new BankAccount(await bt, config, customTheme, api, country);
				await bankAccount.init();
			} catch (e) {
				console.error(e);
			}
		}
	};
};

export { OdinPay };
